'use client'

import _ from 'lodash'
import { Fragment, Key, MouseEventHandler, useState } from 'react'
import styled, { css } from 'styled-components'

import Divider from '@/app/2023/consejo/candidatos/components/ByListSummary/Divider'
import Header from '@/app/2023/consejo/candidatos/components/ByListSummary/Header'
import Parties from '@/app/2023/consejo/candidatos/components/ByListSummary/Parties'
import reportAnalytics from '@/app/2023/consejo/components/analyticsEvents'
import { Lista } from '@/app/2023/consejo/types/listas'
import listas from '@/app/2023/consejo/types/listas'
import partidos from '@/app/2023/consejo/types/partidos'
import { colors, spaces } from '@/config/global-parameters'
import { millionOneDecimalFormatter, normalNumberFormatter } from '@/helpers/formatters'
import { showInDesktop, showInMobile } from '@/styles/mediaQueries'

import ShowMoreBase from '../../../../../components/ShowMore'
import { IGanadorWithData } from '../../types/ganadores'

export interface IByListSummary {
  className?: string
  candidates: IGanadorWithData[]
  allCandidates: IGanadorWithData[]
}

export const getByListSummary = ({
  allCandidates,
  candidates,
}: {
  candidates: IGanadorWithData[]
  allCandidates: IGanadorWithData[]
}) => {
  const candidatesByList = _.groupBy(candidates, ({ lista }) => lista)
  const allCandidatesByList = _.groupBy(allCandidates, ({ lista }) => lista)
  return _.orderBy(Object.entries(listas), ([, { order }]) => order)
    .map(([listaId, lista]) => {
      const candidatos = candidatesByList[listaId] || []
      const candidatosPorPartido = _.countBy(candidatos, ({ partido }) =>
        partido.replace('IND-', '')
      )
      const votosLista = _.sumBy(allCandidatesByList[listaId], 'votos')
      return {
        ...lista,
        id: listaId as Lista,
        parties: lista.parties
          .filter((partyName) => !partyName.includes('IND-'))
          .map((id) => ({
            shortName: partidos[id].acronym,
            size: candidatosPorPartido[id] || 0,
          }))
          .sort(({ size: sizeA }, { size: sizeB }) => sizeB - sizeA),
        size: candidatos.length || 0,
        votosLista,
      }
    })
    .sort(({ order: orderA }, { order: orderB }) => orderA - orderB)
}

const ByListSummary = ({ allCandidates, candidates, className }: IByListSummary) => {
  const [showMore, setShowMore] = useState(false)
  const summaries = getByListSummary({ allCandidates, candidates })
  const listasSummaries = summaries.filter(({ id, name }) => name && id !== '5')
  const indigenasSummary = summaries.filter(({ id, name }) => name && id === '5')
  const handleShowMore = () => {
    setShowMore(!showMore)
    if (!showMore) {
      reportAnalytics('show_more_event', 'Resumen por lista')
    }
  }

  const realListsKeys = Object.keys(listas)
    .filter((key) => key)

  return (
    <Wrapper className={className}>
      <ResultsTable>
        <Lists
          count={realListsKeys.length}
          showMore={showMore}
        >
          {listasSummaries.map(({ id, ...summary }) => (
            <List
              key={id}
              showMore={showMore}
              {...summary}
            />
          ))}
          <Divider
            isMainPage={true}
            showMore={showMore}
          />
          {indigenasSummary.map(({ id, ...summary }) => (
            <List
              key={id}
              showMore={showMore}
              {...summary}
            />
          ))}
        </Lists>
      </ResultsTable>
      <ShowMore
        isOpen={showMore}
        onClick={handleShowMore}
      />
    </Wrapper>
  )
}

interface IListSummary {
  key: Key
  parties: {
    shortName: string
    size: number
  }[]
  size: number
  name: string
  color: string
  shortName?: string
  showMore: boolean
  onClick?: MouseEventHandler
  opaque?: boolean
  votosLista: number
}

const List = ({
  color,
  name,
  onClick,
  opaque,
  parties,
  shortName,
  showMore,
  size,
  votosLista,
}: IListSummary) => (
  <Fragment>
    <Header
      color={color}
      name={name}
      onClick={onClick}
      opaque={opaque}
      shortName={shortName}
      showMore={showMore}
      size={size}
    />
    {showMore && (
      <>
        <VotosLista>{`${normalNumberFormatter.format(votosLista)} votos`}</VotosLista>
        <VotosListaMobile>{`${millionOneDecimalFormatter.format(votosLista)}`}</VotosListaMobile>
        <Parties
          opaque={opaque}
          parties={parties}
        />
      </>
    )}
  </Fragment>
)

const ResultsTable = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
`

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${spaces.xs};
  height: min-content;
`

const showMoreStyle = css`
  grid-auto-flow: column;
  row-gap: 0px;
`

const VotosLista = styled.span`
  color: ${colors.colorGrey27};
  font-weight: 500;
  padding-top: ${spaces.xxs};
  font-size: 12px;
  ${showInDesktop}
`

const VotosListaMobile = styled.span`
  color: ${colors.colorGrey27};
  font-weight: 500;
  padding-top: ${spaces.xxs};
  font-size: 10px;
  width: 100%;
  text-align: center;
  ${showInMobile}
`

const Lists = styled.div<{ count: number; showMore: boolean }>`
  display: grid;
  grid: min-content min-content min-content / repeat(${({ count }) => count - 1}, 1fr) 1px min-content;
  gap: ${spaces.s};
  width: 100%;
  @media (max-width: ${spaces.tabletBreakpoint}) {
    grid: max-content min-content min-content / repeat(${({ count }) => count - 1}, 1fr) 1px 1fr;
    ${({ showMore }) => showMore && showMoreStyle}
  }
  ${({ showMore }) => showMore && showMoreStyle}
`

const ShowMore = styled(ShowMoreBase)`
  margin-top: -${spaces.s};
`

export default ByListSummary
